import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Box,
  Text,
  VStack,
  HStack,
  Flex,
  IconButton,
  Icon,
  Badge,
  Tooltip,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { MdClose, MdInfo } from "react-icons/md";
import { FaRegFileAlt } from "react-icons/fa";
import { FiClock } from "react-icons/fi";
import dayjs from "dayjs";
import services from "../../views/Orders/services";
import { EnrichedSupplierForDelivery, SelectedDeliveryOption } from "./types";
import EstimateSplitCard from "./EstimateSplitCard";
import { estimateSectionHeaderProps } from "./utils/estimateSectionHeaderStyles";
import {
  calculateRemainingItemsMRP,
  calculateSupplierFulfillingItemsMRP,
} from "./utils/deliveryItemUtils";
import { SingleInvoiceToggle } from "../../views/Orders/components/SingleInvoiceToggle";

const EstimateExpiryTimer: React.FC<{ expiryTime?: string }> = ({ expiryTime }) => {
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [isExpired, setIsExpired] = useState<boolean>(false);

  useEffect(() => {
    if (!expiryTime) {
      setTimeLeft("-");
      return;
    }

    const calculateTimeLeft = () => {
      const now = new Date();
      const expiry = new Date(expiryTime);
      const diffMs = expiry.getTime() - now.getTime();

      if (diffMs <= 0) {
        setTimeLeft("Expired");
        setIsExpired(true);
        return true;
      }

      const totalSec = Math.floor(diffMs / 1000);
      const hrs = Math.floor(totalSec / 3600);
      const mins = Math.floor((totalSec % 3600) / 60);
      const secs = totalSec % 60;

      let formatted = "";
      if (hrs > 0) {
        formatted += `${String(hrs).padStart(2, "0")}:`;
      }
      formatted += `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
      setTimeLeft(formatted);
      setIsExpired(false);
      return false;
    };

    calculateTimeLeft();
    const intervalId = setInterval(() => {
      const expired = calculateTimeLeft();
      if (expired) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [expiryTime]);

  if (!expiryTime) return null;

  return (
    <Flex
      align="center"
      justify="center"
      bg={isExpired ? "red.50" : "orange.50"}
      border="1px solid"
      borderColor={isExpired ? "red.200" : "orange.200"}
      borderRadius="full"
      px={2.5}
      height="22px"
      gap={1}
    >
      <Icon
        as={FiClock as any}
        boxSize={3}
        color={isExpired ? "red.500" : "orange.500"}
      />
      <Text
        fontSize="0.75rem"
        fontWeight="700"
        color={isExpired ? "red.700" : "orange.700"}
        fontFamily="'Nunito','Inter', sans-serif"
        lineHeight="1"
      >
        {timeLeft}
      </Text>
    </Flex>
  );
};

interface AllEstimatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderNo: string;
  orderData?: any;
}

const AllEstimatesModal: React.FC<AllEstimatesModalProps> = ({
  isOpen,
  onClose,
  orderNo,
  orderData,
}) => {
  const [estimates, setEstimates] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEstimateIndex, setSelectedEstimateIndex] = useState<number>(0);
  const [selectedEstimateExpired, setSelectedEstimateExpired] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && orderNo) {
      setLoading(true);
      setError(null);
      services.getEstimates(orderNo)
        .then((res: any) => {
          const fetchedEstimates = res?.estimates || [];
          setEstimates(fetchedEstimates);
          setSelectedEstimateIndex(0);
        })
        .catch((err) => {
          console.error("Error fetching estimates:", err);
          setError("Failed to fetch estimates. Please try again.");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, orderNo]);

  const selectedEstimate = estimates[selectedEstimateIndex];

  const selectedEstimateExpiryTime = selectedEstimate?.expiryTime;
  useEffect(() => {
    if (!selectedEstimateExpiryTime) {
      setSelectedEstimateExpired(false);
      return;
    }
    const now = new Date();
    const expiry = new Date(selectedEstimateExpiryTime);
    const diffMs = expiry.getTime() - now.getTime();

    if (diffMs <= 0) {
      setSelectedEstimateExpired(true);
      return;
    }

    setSelectedEstimateExpired(false);
    const timerId = setTimeout(() => {
      setSelectedEstimateExpired(true);
    }, diffMs);

    return () => clearTimeout(timerId);
  }, [selectedEstimateExpiryTime]);

  const isSingleInvoiceEnabled = Boolean(
    orderData?.billingDetails?.isSingleGaraazInvoiceRequired ?? false
  );

  // Map backend splits to frontend EnrichedSupplierForDelivery
  const enrichedSuppliers = React.useMemo(() => {
    if (!selectedEstimate || !selectedEstimate.splits) return [];

    return selectedEstimate.splits.map((split: any) => {
      const parts = (split.items || []).map((item: any) => ({
        partId: item.partId,
        partNumber: item.partNumber,
        quantity: item.qty,
        partName: item.partName,
        mrp: item.mrp,
      }));

      const deliveryOptions = (split.deliveryOptions || []).map((option: any) => ({
        _id: option._id,
        cptSlab: option.cptSlab,
        cptMin: option.cptMin,
        cptMax: option.cptMax,
        deliveryCost: option.deliveryCost,
        vehicleType: split.vehicleType,
        deliveryCostDiscountPercentage: option.deliveryCostDiscountPercentage,
        deliveryMode: option.deliveryMode,
        payableBy: option.payableBy,
        payableTo: option.payableTo,
        isSystemGenerated: option.isSystemGenerated,
        cptAudit: option.cptAudit,
      }));

      return {
        splitId: split._id ? String(split._id) : "",
        supplierId: split.supplierId ? String(split.supplierId) : null,
        supplierName: split.supplier?.businessName || (split.supplierId ? "Unknown Supplier" : "Supplier Not Found"),
        distanceKm: undefined,
        supplierCity: split.supplier?.address?.city || undefined,
        parts,
        deliveryOptions,
        buyTime: split.buyTime != null ? String(split.buyTime) : undefined,
        vehicleType: split.vehicleType,
        transactionType: split.transactionType,
        discountPercentage: split.discountPercentage,
        creditNotePercentage: split.creditNotePercentage,
        fulfillmentStatus: split.fulfillmentStatus,
        payableBy: split.payableBy,
        payableTo: split.payableTo,
        status: split.status,
        confirmedDeliveryOptionId: split.confirmedDeliveryOptionId ? String(split.confirmedDeliveryOptionId) : undefined,
      };
    });
  }, [selectedEstimate]);

  const remainingItemsMRP = React.useMemo(() => {
    return calculateRemainingItemsMRP(enrichedSuppliers);
  }, [enrichedSuppliers]);

  const fulfilledItemsMRP = React.useMemo(() => {
    if (!enrichedSuppliers || enrichedSuppliers.length === 0) {
      return 0;
    }

    return enrichedSuppliers.reduce((total: number, supplier: EnrichedSupplierForDelivery) => {
      if (supplier.supplierId === null || supplier.supplierId === undefined) {
        return total;
      }
      return total + calculateSupplierFulfillingItemsMRP(supplier);
    }, 0);
  }, [enrichedSuppliers]);

  const mapSplitToSelection = (split: any): SelectedDeliveryOption | undefined => {
    if (split.status === "HOLD") {
      return {
        supplierId: split.supplierId,
        customerAction: "hold",
      };
    }
    if (split.status === "CANCELLED") {
      return {
        supplierId: split.supplierId,
        customerAction: "cancel",
      };
    }
    if (split.status === "BUYTIME_TAKEN") {
      const hours = split.buyTime ? Math.round((split.buyTime / 60) * 10) / 10 : 0;
      return {
        supplierId: split.supplierId,
        customerAction: "buytime",
        buyTimeHours: hours > 0 ? String(hours) : "",
      };
    }
    if (split.status === "CONFIRMED" || split.confirmedDeliveryOptionId) {
      return {
        supplierId: split.supplierId,
        customerAction: "confirm",
        deliveryDetails: {
          selectionType: "option",
          selectedDeliveryOptionId: split.confirmedDeliveryOptionId ? String(split.confirmedDeliveryOptionId) : undefined,
        },
      };
    }
    return undefined;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="xl" h="80vh" maxH="800px" minH="500px" overflow="hidden">
        <ModalHeader borderBottom="1px solid" borderBottomColor="gray.200" py={3} px={5}>
          <Flex align="center" justify="space-between">
            <HStack spacing={3}>
              <Text fontSize="lg" fontWeight="800" color="gray.800" fontFamily="'Nunito','Inter', sans-serif" textTransform="uppercase" letterSpacing="wide">
                All Estimates
              </Text>
              <Flex
                align="center"
                bg="purple.50"
                border="1px solid"
                borderColor="purple.200"
                px={3}
                py={1}
                borderRadius="full"
              >
                <Text fontSize="0.9rem" fontWeight="800" color="purple.700" fontFamily="'Nunito','Inter', sans-serif" textTransform="uppercase">
                  Enquiry: {orderNo}
                </Text>
              </Flex>
            </HStack>
            <IconButton
              aria-label="Close modal"
              icon={<MdClose size={20} />}
              onClick={onClose}
              variant="ghost"
              size="sm"
              borderRadius="full"
            />
          </Flex>
        </ModalHeader>

        <ModalBody p={4} display="flex" flex={1} overflow="hidden" bg="gray.50">
          {loading ? (
            <Flex
              w="100%"
              h="100%"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              overflow="hidden"
              boxShadow="sm"
            >
              {/* Left Panel Skeleton */}
              <Box
                w="280px"
                flexShrink={0}
                borderRight="1px solid"
                borderColor="gray.200"
                p={3}
                bg="white"
              >
                <VStack spacing={3} align="stretch">
                  {[1, 2, 3, 4].map((i) => (
                    <Box
                      key={i}
                      p={3}
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.100"
                    >
                      <Flex justify="space-between" align="center" mb={2}>
                        <Skeleton height="16px" width="100px" startColor="gray.100" endColor="gray.200" />
                        <Skeleton height="16px" width="50px" borderRadius="md" startColor="gray.100" endColor="gray.200" />
                      </Flex>
                      <Skeleton height="12px" width="140px" startColor="gray.100" endColor="gray.200" />
                    </Box>
                  ))}
                </VStack>
              </Box>

              {/* Right Panel Skeleton */}
              <Box flex={1} p={5} bg="gray.50" overflowY="auto">
                <VStack spacing={5} align="stretch">
                  {/* Top Bar Skeleton */}
                  <Flex justify="space-between" align="flex-start" borderBottom="1px solid" borderColor="gray.200" pb={3}>
                    <Box>
                      <Skeleton height="20px" width="120px" mb={2} startColor="gray.100" endColor="gray.200" />
                      <Skeleton height="14px" width="300px" startColor="gray.100" endColor="gray.200" />
                    </Box>
                    <VStack align="flex-end" spacing={2}>
                      <Skeleton height="20px" width="150px" startColor="gray.100" endColor="gray.200" />
                      <Skeleton height="16px" width="100px" startColor="gray.100" endColor="gray.200" />
                    </VStack>
                  </Flex>

                  {/* Estimate Cards Skeleton */}
                  {[1, 2].map((i) => (
                    <Box
                      key={i}
                      bg="white"
                      borderRadius="lg"
                      border="1px solid"
                      borderColor="gray.200"
                      overflow="hidden"
                      p={4}
                    >
                      <Flex justify="space-between" align="center" mb={4}>
                        <Skeleton height="20px" width="200px" startColor="gray.100" endColor="gray.200" />
                        <Skeleton height="32px" width="100px" borderRadius="full" startColor="gray.100" endColor="gray.200" />
                      </Flex>
                      <Flex gap={4}>
                        <Box flex={1}>
                          <SkeletonText noOfLines={3} spacing="3" startColor="gray.100" endColor="gray.200" />
                        </Box>
                        <Box w="300px">
                          <Skeleton height="80px" borderRadius="md" startColor="gray.100" endColor="gray.200" />
                        </Box>
                      </Flex>
                    </Box>
                  ))}
                </VStack>
              </Box>
            </Flex>
          ) : error ? (
            <Flex
              align="center"
              justify="center"
              w="100%"
              h="100%"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              boxShadow="sm"
              direction="column"
              gap={3}
            >
              <Text color="red.500" fontWeight="600" fontFamily="'Nunito','Inter', sans-serif">{error}</Text>
            </Flex>
          ) : estimates.length === 0 ? (
            <Flex
              align="center"
              justify="center"
              w="100%"
              h="100%"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              boxShadow="sm"
              direction="column"
              gap={4}
              py={10}
            >
              <Icon as={FaRegFileAlt} boxSize={12} color="gray.300" />
              <Text color="gray.500" fontWeight="700" fontSize="lg" fontFamily="'Nunito','Inter', sans-serif">
                No Estimates Found
              </Text>
              <Text color="gray.400" fontSize="sm" textAlign="center" maxW="320px" fontFamily="'Nunito','Inter', sans-serif">
                There are no estimates created for this enquiry yet.
              </Text>
            </Flex>
          ) : (
            <Flex
              w="100%"
              h="100%"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              overflow="hidden"
              boxShadow="sm"
            >
              {/* Left Panel - Estimates List */}
              <Box
                w="280px"
                flexShrink={0}
                borderRight="1px solid"
                borderColor="gray.200"
                overflowY="auto"
                p={3}
                bg="white"
                css={{
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c1c1c1",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#a8a8a8",
                  },
                }}
              >
                <VStack spacing={2.5} align="stretch">
                  {estimates.map((est: any, idx: number) => {
                    const isSelected = idx === selectedEstimateIndex;
                    const dateFormatted = dayjs(est.createdAt).format("DD MMM YYYY, hh:mm A");
                    return (
                      <Box
                        key={est.estimateNo}
                        onClick={() => setSelectedEstimateIndex(idx)}
                        p={3}
                        borderRadius="md"
                        cursor="pointer"
                        transition="all 0.2s"
                        bg={isSelected ? "purple.50" : "white"}
                        border="1px solid"
                        borderColor={isSelected ? "purple.200" : "gray.100"}
                        borderLeft={isSelected ? "4px solid" : "4px solid transparent"}
                        borderLeftColor={isSelected ? "purple.500" : "transparent"}
                        boxShadow="sm"
                        _hover={isSelected ? {} : { bg: "gray.50", borderColor: "gray.200" }}
                      >
                        <Flex justify="space-between" align="center" mb={1}>
                          <Text
                            fontSize="0.85rem"
                            fontWeight="800"
                            color={isSelected ? "purple.700" : "gray.700"}
                            fontFamily="'Nunito','Inter', sans-serif"
                          >
                            {est.estimateNo}
                          </Text>
                          <Badge
                            colorScheme={isSelected ? "purple" : "gray"}
                            borderRadius="md"
                            px={1.5}
                            fontSize="0.65rem"
                            fontFamily="'Nunito','Inter', sans-serif"
                          >
                            {est.splits?.length || 0} splits
                          </Badge>
                        </Flex>
                        <Text fontSize="0.75rem" color="gray.500" fontWeight="500" fontFamily="'Nunito','Inter', sans-serif" mt={1.5}>
                          {dateFormatted}
                        </Text>
                      </Box>
                    );
                  })}
                </VStack>
              </Box>

              {/* Right Panel - Estimate Details */}
              <Box
                flex={1}
                overflowY="auto"
                p={5}
                bg="gray.50"
                css={{
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c1c1c1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#a8a8a8",
                  },
                }}
              >
                <VStack spacing={5} align="stretch">
                  {/* Selected Estimate Top Info Bar */}
                  <Box borderBottom="1px solid" borderBottomColor="gray.200" pb={3.5}>
                    <Flex justify="space-between" align="flex-start">
                      <Box>
                        <Text {...estimateSectionHeaderProps}>
                          Suppliers
                        </Text>
                        <Text
                          fontSize="0.8rem"
                          color="gray.500"
                          fontFamily="'Nunito','Inter', sans-serif"
                          maxW="600px"
                        >
                          Review part details and configurations for all split(s).
                        </Text>
                      </Box>
                      <VStack align="flex-end" spacing={1.5}>
                        {selectedEstimate?.expiryTime && (
                          <Flex align="center" gap={1.5} height="22px">
                            {!selectedEstimateExpired && (
                              <Text
                                fontSize="0.75rem"
                                fontWeight="800"
                                color="gray.500"
                                fontFamily="'Nunito','Inter', sans-serif"
                                textTransform="uppercase"
                                letterSpacing="0.02em"
                                lineHeight="1"
                              >
                                EXPIRES IN:
                              </Text>
                            )}
                            <EstimateExpiryTimer expiryTime={selectedEstimate.expiryTime} />
                          </Flex>
                        )}

                        <Tooltip
                          label="Total MRP of Available Parts / Total MRP of Unavailable Parts"
                          hasArrow
                          placement="top"
                          shouldWrapChildren
                        >
                          <HStack align="center" spacing={2} cursor="help">
                            <Text {...estimateSectionHeaderProps} whiteSpace="nowrap">
                              MRP:
                            </Text>
                            <Text {...estimateSectionHeaderProps} whiteSpace="nowrap">
                              ₹{fulfilledItemsMRP.toLocaleString("en-IN")}
                            </Text>
                            <Text {...estimateSectionHeaderProps}>
                              /
                            </Text>
                            <Text
                              {...estimateSectionHeaderProps}
                              color="red.700"
                              whiteSpace="nowrap"
                            >
                              ₹{remainingItemsMRP.toLocaleString("en-IN")}
                            </Text>
                          </HStack>
                        </Tooltip>

                        <HStack spacing={1} align="center">
                          <SingleInvoiceToggle
                            orderId={orderData?._id || ""}
                            isChecked={isSingleInvoiceEnabled}
                            isLocked={true}
                            refetchAllOrders={() => { }}
                          />
                          <Tooltip
                            label="Enabling this will allow you to create a single invoice for all confirmed orders. No individual invoice will be created automatically."
                            hasArrow
                            placement="top"
                          >
                            <Box as="span" display="inline-flex" cursor="help">
                              <Icon as={MdInfo} boxSize={4} color="gray.500" />
                            </Box>
                          </Tooltip>
                        </HStack>
                      </VStack>
                    </Flex>
                  </Box>

                  {enrichedSuppliers.map((supplier: EnrichedSupplierForDelivery, idx: number) => {
                    const rawSplit = selectedEstimate.splits[idx];
                    return (
                      <EstimateSplitCard
                        key={supplier.splitId}
                        estimateNo={selectedEstimate.estimateNo}
                        orderNo={orderNo}
                        supplier={supplier}
                        selection={mapSplitToSelection(rawSplit)}
                        isReadOnly={true}
                        isExpired={selectedEstimateExpired}
                      />
                    );
                  })}
                </VStack>
              </Box>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>

  );
};

export default AllEstimatesModal;
